<script>
import { mapState, mapActions, mapGetters } from 'vuex';

export default {
  name: 'TransportationRouteTextsInformation',
  props: {
    shown: {
      required: true,
      type: Boolean,
    },
  },
  data() {
    return {
      headers: [
        { text: 'ID', class: 'px2', value: 'id', sortable: false },
        { text: 'Route ID', class: 'px2', value: 'routeId', sortable: false },
        { text: 'Text', class: 'px2', width: '400px', value: 'text', sortable: false },
        { text: 'Tags', class: 'px2', width: '400px', sortable: false },
        { text: 'Actions', class: 'px2', width: '265px', sortable: false },
      ],
      tagsRule: [v => v.length > 0 || 'At least one tag is required'],
      textRule: [v => !!v || 'Text cannot be empty'],
      showAddTextDialog: false,
      addTextFormValid: false,
      addText: {
        tagIds: [],
        text: '',
      },
    };
  },
  computed: {
    ...mapState({
      route: state => state.transportation.routes.route,
      loading: state => state.transportation.routes.texts.loading,
      error: state => state.transportation.routes.texts.error,
    }),
    ...mapGetters({
      texts: 'transportation/getRouteTexts',
      tags: 'transportation/getRouteTags',
    }),
  },
  methods: {
    ...mapActions({
      getRouteTexts: 'transportation/getRouteTextsAndTags',
      createRouteText: 'transportation/createRouteText',
      updateRouteText: 'transportation/updateRouteText',
      deleteRouteText: 'transportation/deleteRouteText',
      changeRouteText: 'transportation/changeRouteText',
      clearRouteList: 'transportation/clearRouteTextsList',
    }),
    closeAddTextDialog(action) {
      if (action === 'save') {
        this.createRouteText({ routeId: this.route.id, text: this.addText });
      }

      this.$refs.addTextForm.resetValidation();
      this.showAddTextDialog = false;
      this.addText = {
        tagIds: [],
        text: '',
      };
    },
    validateAddTextInput() {
      if (this.$refs.addTextForm.validate()) {
        this.closeAddTextDialog('save');
      }
    },
    changeItem(payload) {
      this.changeRouteText({
        ...payload.item,
        ...payload.params,
        notSaved: true,
      });
    },
    updateItem(payload) {
      const tagIds = payload.tags.map(tag => tag.id);

      const body = {
        tagIds,
        text: payload.text,
      };

      this.updateRouteText({ routeId: this.route.id, textId: payload.id, body });
    },
    close() {
      this.clearRouteList();
      this.$emit('close');
    },
  },
  watch: {
    shown() {
      if (this.shown) {
        this.getRouteTexts(this.route.id);
      }
    },
  },
};
</script>

<template>
  <v-card v-if="shown">
    <v-card-title>
      <h3>
        {{ 'Texts for route ' }}
        "{{ route.originWebName }} - {{ route.destinationWebName }}"
      </h3>
    </v-card-title>
    <v-btn
      :disabled="loading"
      color="green"
      outline
      @click="showAddTextDialog = true">
      Create text
    </v-btn>
    <v-data-table
      :items="texts"
      :headers="headers"
      :loading="loading"
      hide-actions
    >
      <template v-slot:no-data>
        <tr v-if="!error && !loading">
          <td colspan="5" class="text-xs-center">No results found</td>
        </tr>
        <tr v-if="error && !loading">
          <td colspan="5" class="text-xs-center">An error occurred. Contact IT.</td>
        </tr>
      </template>
      <template v-slot:items="data">
        <td>
          {{ data.item.id }}
        </td>
        <td>
          {{ data.item.routeId }}
        </td>
        <td>
          <v-textarea
            @input="changeItem({params: {text: $event}, item: data.item})"
            :value="data.item.text"
            :rules="textRule"
            box
            auto-grow
          >
            {{ data.item.text }}
          </v-textarea>
        </td>
        <td>
          <v-select
            @input="changeItem({params: {tags: $event}, item: data.item})"
            :items="tags"
            :value="data.item.tags"
            :rules="tagsRule"
            name="tag"
            item-text="label"
            item-value="id"
            return-object
            chips
            deletable-chips
            multiple
          />
        </td>
        <td class="text-xs-right">
          <v-flex>
            <v-btn
              :disabled="!data.item.notSaved || !data.item.tags.length || !data.item.text || loading"
              color="green"
              outline
              @click="updateItem(data.item)">
              Save
            </v-btn>
            <v-btn
              :disabled="loading"
              color="red"
              outline
              @click="deleteRouteText({routeId: route.id, textId: data.item.id})">
              Delete
            </v-btn>
          </v-flex>
        </td>
      </template>
    </v-data-table>
    <v-dialog
      v-model="showAddTextDialog"
      @keydown.esc="showAddTextDialog = false"
      max-width="1200px"
      no-click-animation
      persistent>
      <v-card>
        <v-flex>
          <v-card-title>
            <h2>Add text</h2>
          </v-card-title>
          <v-card-text>
            <v-form
              ref="addTextForm"
              v-model="addTextFormValid"
              lazy-validation>
              <v-container grid-list-md class="pa-0">
                <v-layout row wrap>
                  <v-flex xs1 class="font-weight-bold">
                    Text
                  </v-flex>
                  <v-flex xs6>
                    <v-textarea
                      v-model="addText.text"
                      :rules="textRule"
                      box
                      auto-grow
                      required
                    />
                  </v-flex>
                </v-layout>
                <v-layout row wrap>
                  <v-flex xs1 class="font-weight-bold">
                    Tags
                  </v-flex>
                  <v-flex xs6>
                    <v-select
                      v-model="addText.tagIds"
                      :items="tags"
                      :rules="tagsRule"
                      name="tag"
                      item-text="label"
                      item-value="id"
                      label="Select tags"
                      chips
                      deletable-chips
                      multiple
                      required
                    />
                  </v-flex>
                </v-layout>
              </v-container>
            </v-form>
          </v-card-text>
        </v-flex>
        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="!addTextFormValid"
            color="green"
            flat
            @click="validateAddTextInput"
          >
            Save
          </v-btn>
          <v-btn
            color="blue darken-1"
            flat
            @click="closeAddTextDialog('close')">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-card-actions>
      <v-spacer/>
      <v-btn
        :disabled="loading"
        color="blue darken-1"
        flat
        @click="close">
        Close
      </v-btn>
    </v-card-actions>
  </v-card>
</template>



// WEBPACK FOOTER //
// src/components/transportation/TransportationRouteTextsInformation.vue